import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X, ChevronLeft, Upload, Loader2 } from 'lucide-react';
import { supabase, uploadImage } from './supabaseClient';

// ============================================================
// CONSTANTS
// ============================================================
const FINE_ART_CATEGORIES = [
  { id: 'landscapes', name: 'Landscapes' },
  { id: 'portraits', name: 'Portraits' },
  { id: 'florals', name: 'Florals' },
  { id: 'murals', name: 'Murals' },
];

// Default collection categories — loaded dynamically from database
const DEFAULT_COLLECTION_CATEGORIES = [
  { id: 'tanzania', name: 'Tanzania' },
  { id: 'interiors', name: 'Interiors' },
];

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_rubyqd5';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'HeF9BFIGymFw-NfP0';

// Prodigi product catalog — SKU to friendly name mapping
// Rosanna can enable any of these per artwork from the admin panel
const PRODUCT_CATALOG = [
  { sku: 'GLOBAL-FAP-A4', name: 'Fine Art Print', size: 'A4', category: 'print', defaultPrice: 35 },
  { sku: 'GLOBAL-FAP-A3', name: 'Fine Art Print', size: 'A3', category: 'print', defaultPrice: 55 },
  { sku: 'GLOBAL-FAP-A2', name: 'Fine Art Print', size: 'A2', category: 'print', defaultPrice: 85 },
  { sku: 'GLOBAL-FAP-A1', name: 'Fine Art Print', size: 'A1', category: 'print', defaultPrice: 120 },
  { sku: 'GLOBAL-FAP-16x20', name: 'Fine Art Print', size: '16×20"', category: 'print', defaultPrice: 65 },
  { sku: 'GLOBAL-FAP-16x24', name: 'Fine Art Print', size: '16×24"', category: 'print', defaultPrice: 85 },
  { sku: 'GLOBAL-FAP-24x36', name: 'Fine Art Print', size: '24×36"', category: 'print', defaultPrice: 130 },
  { sku: 'GLOBAL-FRP-A4-BK', name: 'Framed Print (Black)', size: 'A4', category: 'framed', defaultPrice: 75 },
  { sku: 'GLOBAL-FRP-A3-BK', name: 'Framed Print (Black)', size: 'A3', category: 'framed', defaultPrice: 110 },
  { sku: 'GLOBAL-FRP-A2-BK', name: 'Framed Print (Black)', size: 'A2', category: 'framed', defaultPrice: 160 },
  { sku: 'GLOBAL-FRP-A4-WH', name: 'Framed Print (White)', size: 'A4', category: 'framed', defaultPrice: 75 },
  { sku: 'GLOBAL-FRP-A3-WH', name: 'Framed Print (White)', size: 'A3', category: 'framed', defaultPrice: 110 },
  { sku: 'GLOBAL-FRP-A2-WH', name: 'Framed Print (White)', size: 'A2', category: 'framed', defaultPrice: 160 },
  { sku: 'GLOBAL-FRP-A4-NT', name: 'Framed Print (Natural)', size: 'A4', category: 'framed', defaultPrice: 75 },
  { sku: 'GLOBAL-FRP-A3-NT', name: 'Framed Print (Natural)', size: 'A3', category: 'framed', defaultPrice: 110 },
  { sku: 'GLOBAL-FRP-A2-NT', name: 'Framed Print (Natural)', size: 'A2', category: 'framed', defaultPrice: 160 },
];

// ============================================================
// IMAGE UPLOAD COMPONENT
// ============================================================
const ImageUploader = ({ currentUrl, onUploaded, folder = 'artworks', label = 'Upload Image' }) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (JPG, PNG, etc.)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Image too large. Please use an image under 10MB.');
      return;
    }

    setUploading(true);
    try {
      const url = await uploadImage(file, folder);
      onUploaded(url);
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed. Please try again.');
    }
    setUploading(false);
    e.target.value = ''; // reset input
  };

  return (
    <div>
      {currentUrl && (
        <img src={currentUrl} alt="" className="w-full h-32 object-contain bg-gray-100 rounded-lg mb-2" />
      )}
      <label className={`inline-flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg cursor-pointer transition ${uploading ? 'opacity-50 pointer-events-none' : 'hover:bg-gray-50'}`}>
        {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        {uploading ? 'Uploading...' : label}
        <input type="file" accept="image/*" onChange={handleUpload} className="hidden" disabled={uploading} />
      </label>
    </div>
  );
};

// ============================================================
// MAIN APP
// ============================================================
const RosannaPrescottArt = () => {
  // UI State
  const [currentPage, setCurrentPage] = useState('home');
  const [sectionTitle, setSectionTitle] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [adminMode, setAdminMode] = useState(false);
  const [adminTab, setAdminTab] = useState('artworks');
  const [adminCategory, setAdminCategory] = useState('landscapes');
  const [passwordInput, setPasswordInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCommissionForm, setShowCommissionForm] = useState(false);
  const [showEnquiryForm, setShowEnquiryForm] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [editingPost, setEditingPost] = useState(null);

  // Data State
  const [artworks, setArtworks] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [printProducts, setPrintProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState({ hero_image: '', about_image: '', about_text: '' });
  const [commissions, setCommissions] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [dbError, setDbError] = useState(null);
  const [shopArtwork, setShopArtwork] = useState(null); // artwork selected in shop
  const [checkingOut, setCheckingOut] = useState(false);
  const [collectionCategories, setCollectionCategories] = useState(DEFAULT_COLLECTION_CATEGORIES);

  // Derived
  const ALL_CATEGORIES = [...FINE_ART_CATEGORIES, ...collectionCategories];

  // ============================================================
  // DATA LOADING
  // ============================================================
  const loadData = useCallback(async () => {
    if (!supabase) {
      setDbError('Supabase not configured.');
      setDataLoaded(true);
      return;
    }
    try {
      const [artRes, settRes, blogRes, ppRes] = await Promise.all([
        supabase.from('artworks').select('*').order('sort_order').order('created_at'),
        supabase.from('site_settings').select('*'),
        supabase.from('blog_posts').select('*').order('created_at', { ascending: false }),
        supabase.from('print_products').select('*'),
      ]);
      if (artRes.error) throw artRes.error;
      if (settRes.error) throw settRes.error;

      setArtworks(artRes.data || []);
      setBlogPosts(blogRes.data || []);
      setPrintProducts(ppRes.data || []);

      const s = {};
      (settRes.data || []).forEach(r => { s[r.key] = r.value; });
      setSettings(prev => ({ ...prev, ...s }));

      // Load dynamic collection categories
      if (s.collection_categories) {
        try {
          const parsed = JSON.parse(s.collection_categories);
          if (Array.isArray(parsed) && parsed.length > 0) setCollectionCategories(parsed);
        } catch (e) { /* use defaults */ }
      }

      setDataLoaded(true);
    } catch (err) {
      console.error('Load failed:', err);
      setDbError('Failed to connect to database.');
      setDataLoaded(true);
    }
  }, []);

  const loadCommissions = useCallback(async () => {
    if (!supabase) return;
    const { data } = await supabase.from('commissions').select('*').order('created_at', { ascending: false });
    setCommissions(data || []);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ============================================================
  // ADMIN: CRUD
  // ============================================================
  const saveArtwork = async (artwork) => {
    if (!supabase) return;
    setSaving(true);
    try {
      const { id, created_at, updated_at, ...fields } = artwork;
      if (id) await supabase.from('artworks').update(fields).eq('id', id);
      else await supabase.from('artworks').insert(fields);
      await loadData();
    } catch (err) {
      console.error('Save failed:', err);
      alert('Failed to save. Please try again.');
    }
    setSaving(false);
  };

  const deleteArtwork = async (id) => {
    if (!supabase || !window.confirm('Delete this artwork?')) return;
    setSaving(true);
    await supabase.from('artworks').delete().eq('id', id);
    await loadData();
    setSaving(false);
  };

  const addNewArtwork = async (category) => {
    if (!supabase) return;
    setSaving(true);
    const maxSort = artworks.filter(a => a.category === category).reduce((m, a) => Math.max(m, a.sort_order || 0), 0);
    await supabase.from('artworks').insert({
      title: 'New Artwork', category, image_url: '', medium: '', size: '',
      status: 'portfolio', price: 0, print_available: false, print_price: 0, sort_order: maxSort + 1,
    });
    await loadData();
    setSaving(false);
  };

  const saveSetting = async (key, value) => {
    if (!supabase) return;
    setSaving(true);
    await supabase.from('site_settings').upsert({ key, value, updated_at: new Date().toISOString() });
    setSettings(prev => ({ ...prev, [key]: value }));
    setSaving(false);
  };

  const saveBlogPost = async (post) => {
    if (!supabase) return;
    setSaving(true);
    const { id, created_at, updated_at, ...fields } = post;
    if (id) await supabase.from('blog_posts').update(fields).eq('id', id);
    else await supabase.from('blog_posts').insert(fields);
    await loadData();
    setSaving(false);
  };

  const addBlogPost = async () => {
    if (!supabase) return;
    setSaving(true);
    const slug = 'post-' + Date.now();
    const { data } = await supabase.from('blog_posts').insert({
      title: 'New Post', slug, content: '', cover_image: '', published: false,
    }).select();
    await loadData();
    if (data?.[0]) setEditingPost(data[0].id);
    setSaving(false);
  };

  const deleteBlogPost = async (id) => {
    if (!supabase || !window.confirm('Delete this post?')) return;
    setSaving(true);
    await supabase.from('blog_posts').delete().eq('id', id);
    if (editingPost === id) setEditingPost(null);
    await loadData();
    setSaving(false);
  };

  // ============================================================
  // PRINT PRODUCTS CRUD
  // ============================================================
  const addPrintProduct = async (artworkId, catalogItem) => {
    if (!supabase) return;
    setSaving(true);
    await supabase.from('print_products').insert({
      artwork_id: artworkId,
      sku: catalogItem.sku,
      product_name: `${catalogItem.name} — ${catalogItem.size}`,
      size_label: catalogItem.size,
      retail_price: catalogItem.defaultPrice,
      enabled: true,
    });
    await loadData();
    setSaving(false);
  };

  const updatePrintProduct = async (id, field, value) => {
    if (!supabase) return;
    await supabase.from('print_products').update({ [field]: value }).eq('id', id);
    setPrintProducts(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const deletePrintProduct = async (id) => {
    if (!supabase) return;
    setSaving(true);
    await supabase.from('print_products').delete().eq('id', id);
    await loadData();
    setSaving(false);
  };

  const loadOrders = useCallback(async () => {
    if (!supabase) return;
    const { data } = await supabase.from('orders').select('*').order('created_at', { ascending: false });
    setOrders(data || []);
  }, []);

  // ============================================================
  // CHECKOUT (Stripe)
  // ============================================================
  const handleCheckout = async (artwork, product) => {
    setCheckingOut(true);
    try {
      const res = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            name: `${artwork.title} — ${product.product_name}`,
            description: `${artwork.medium || 'Fine Art'} by Rosanna Prescott`,
            image_url: artwork.image_url,
            price_gbp: Number(product.retail_price),
            quantity: 1,
            sku: product.sku,
            artwork_id: artwork.id,
            print_product_id: product.id,
          }],
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Failed to start checkout. Please try again.');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Something went wrong. Please try again.');
    }
    setCheckingOut(false);
  };

  // ============================================================
  // EMAIL
  // ============================================================
  const sendEmail = async (templateId, params) => {
    if (!window.emailjs) return false;
    try {
      await window.emailjs.send(EMAILJS_SERVICE_ID, templateId, params, EMAILJS_PUBLIC_KEY);
      return true;
    } catch (err) {
      console.error('Email failed:', err);
      return false;
    }
  };

  const handleCommissionSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    const fd = new FormData(e.target);
    const data = { email: fd.get('email'), category: fd.get('category'), message: fd.get('message') };
    if (supabase) await supabase.from('commissions').insert({ ...data, enquiry_type: 'commission' });
    await sendEmail('template_l4qctpd', { ...data, name: data.email.split('@')[0] });
    setFormSubmitting(false);
    setShowCommissionForm(false);
    alert('Thank you! Your enquiry has been sent.');
  };

  const handleArtworkEnquiry = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    const fd = new FormData(e.target);
    const artwork = showEnquiryForm;
    const data = {
      email: fd.get('email'),
      message: fd.get('message') || `I'm interested in "${artwork.title}"`,
    };
    if (supabase) await supabase.from('commissions').insert({
      email: data.email, artwork_title: artwork.title, category: artwork.category,
      message: data.message, enquiry_type: artwork._printEnquiry ? 'print' : 'purchase',
    });
    await sendEmail('template_l4qctpd', {
      email: data.email, artwork_title: artwork.title, category: artwork.category,
      message: data.message, name: data.email.split('@')[0],
    });
    setFormSubmitting(false);
    setShowEnquiryForm(null);
    alert('Thank you! Your enquiry has been sent to Rosanna.');
  };

  // ============================================================
  // NAVIGATION (with browser history support)
  // ============================================================
  const navigateTo = (page, title = '', pushHistory = true) => {
    setCurrentPage(page); setSectionTitle(title); setMenuOpen(false);
    setSelectedCategory(null); setSelectedPost(null); setShopArtwork(null);
    window.scrollTo(0, 0);
    if (pushHistory) {
      window.history.pushState({ page, title }, '', `/${page === 'home' ? '' : page}`);
    }
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (e) => {
      if (e.state?.page) {
        navigateTo(e.state.page, e.state.title || '', false);
      } else {
        navigateTo('home', '', false);
      }
    };
    // Set initial history state
    window.history.replaceState({ page: 'home', title: '' }, '', '/');
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === 'rosanna123') { setAdminMode(true); setPasswordInput(''); loadCommissions(); loadOrders(); }
    else alert('Incorrect password');
  };

  // ============================================================
  // HELPERS
  // ============================================================
  const artworksByCategory = (cat) => artworks.filter(a => a.category === cat);
  const getCategoryImage = (cat) => artworksByCategory(cat).find(a => a.image_url)?.image_url || '';
  const publishedPosts = blogPosts.filter(p => p.published);
  const getArtworkProducts = (artworkId) => printProducts.filter(p => p.artwork_id === artworkId && p.enabled);
  const artworksWithPrints = artworks.filter(a => getArtworkProducts(a.id).length > 0);

  // Check for checkout success/cancel in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      alert('Thank you for your order! You will receive a confirmation email shortly.');
      window.history.replaceState({}, '', '/');
    } else if (params.get('checkout') === 'cancelled') {
      window.history.replaceState({}, '', '/');
    }
  }, []);

  const renderBlogContent = (text) => (text || '').split('\n\n').map((block, i) => {
    if (block.startsWith('*') && block.endsWith('*')) return <em key={i}>{block.slice(1, -1)}</em>;
    return <p key={i}>{block}</p>;
  });

  // ============================================================
  // LOADING
  // ============================================================
  if (!dataLoaded) return (
    <div className="flex items-center justify-center h-screen bg-gradient-to-br from-white via-pink-50 to-white">
      <div className="text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800 mx-auto mb-4"></div><p className="text-gray-500 font-light">Loading...</p></div>
    </div>
  );

  if (dbError && !adminMode) return (
    <div className="flex items-center justify-center h-screen bg-gradient-to-br from-white via-pink-50 to-white p-8">
      <div className="text-center max-w-md"><h1 className="text-3xl font-light text-gray-800 mb-4">Rosanna Prescott Art</h1><p className="text-gray-500 mb-6">{dbError}</p></div>
    </div>
  );

  // ============================================================
  // ARTWORK CARD (masonry)
  // ============================================================
  const ArtworkCard = ({ artwork }) => {
    const hasImage = artwork.image_url?.trim();
    return (
      <div className="masonry-item group">
        <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-4 shadow-md cursor-pointer"
          onClick={() => hasImage && setLightboxImage(artwork.image_url)}>
          {hasImage ? (
            <img src={artwork.image_url} alt={artwork.title} className="w-full h-auto block group-hover:scale-105 transition duration-700" loading="lazy" />
          ) : (
            <div className="flex items-center justify-center py-24 text-gray-300"><span className="text-5xl">🎨</span></div>
          )}
          {artwork.status === 'portfolio' && (
            <div className="absolute top-3 left-3 bg-white/90 text-gray-600 text-xs px-3 py-1 rounded-full tracking-widest">SOLD</div>
          )}
        </div>
        <h3 className="text-lg font-light text-gray-800 mb-0.5">{artwork.title}</h3>
        {artwork.medium && <p className="text-sm text-gray-400 font-light">{artwork.medium}</p>}
        {artwork.size && <p className="text-sm text-gray-400 font-light mb-3">{artwork.size}</p>}
        <div className="flex flex-col gap-1.5">
          {artwork.status === 'for_sale' && artwork.price > 0 && (
            <button onClick={() => setShowEnquiryForm(artwork)} className="w-full py-2 bg-gray-800 text-white text-sm font-light rounded-lg hover:bg-gray-900 transition">
              Enquire — £{Number(artwork.price).toLocaleString()}
            </button>
          )}
          {artwork.status === 'portfolio' && (
            <button onClick={() => setShowCommissionForm(true)} className="w-full py-2 border border-gray-300 text-gray-600 text-sm font-light rounded-lg hover:bg-gray-50 transition">
              Commission Similar
            </button>
          )}
          {getArtworkProducts(artwork.id).length > 0 && (
            <button onClick={() => { setCurrentPage('shop'); setShopArtwork(artwork); setSectionTitle('Shop'); }} className="w-full py-2 border border-gray-800 text-gray-800 text-sm font-light rounded-lg hover:bg-gray-50 transition">
              Buy Print — from £{Math.min(...getArtworkProducts(artwork.id).map(p => Number(p.retail_price))).toLocaleString()}
            </button>
          )}
        </div>
      </div>
    );
  };

  // ============================================================
  // ADMIN PANEL
  // ============================================================
  const AdminPanel = () => {
    const [local, setLocal] = useState({});
    const [localSettings, setLocalSettings] = useState({ ...settings });
    const [localPosts, setLocalPosts] = useState({});
    const [unsaved, setUnsaved] = useState(new Set());
    const [unsavedPosts, setUnsavedPosts] = useState(new Set());

    useEffect(() => {
      const m = {}; artworks.forEach(a => { m[a.id] = { ...a }; }); setLocal(m);
      const p = {}; blogPosts.forEach(b => { p[b.id] = { ...b }; }); setLocalPosts(p);
      setUnsaved(new Set()); setUnsavedPosts(new Set());
    }, []);

    const updateLocal = (id, field, value) => {
      setLocal(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
      setUnsaved(prev => new Set(prev).add(id));
    };

    const saveOne = async (id) => {
      await saveArtwork(local[id]);
      setUnsaved(prev => { const n = new Set(prev); n.delete(id); return n; });
    };

    const updateLocalPost = (id, field, value) => {
      setLocalPosts(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
      setUnsavedPosts(prev => new Set(prev).add(id));
    };

    const saveOnePost = async (id) => {
      await saveBlogPost(localPosts[id]);
      setUnsavedPosts(prev => { const n = new Set(prev); n.delete(id); return n; });
    };

    const catArtworks = Object.values(local)
      .filter(a => a.category === adminCategory)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

    const tabs = [
      { id: 'artworks', label: 'Artworks' },
      { id: 'journal', label: `Journal (${blogPosts.length})` },
      { id: 'hero', label: 'Hero' },
      { id: 'about', label: 'About' },
      { id: 'enquiries', label: `Enquiries (${commissions.length})` },
      { id: 'orders', label: `Orders (${orders.length})` },
    ];

    return (
      <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
        <div className="bg-white w-full max-w-6xl mx-auto min-h-screen shadow-2xl">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-2xl font-light text-gray-800">Admin Panel</h1>
              <div className="flex items-center gap-3">
                {saving && <span className="text-sm text-gray-400">Saving...</span>}
                <button onClick={() => setAdminMode(false)} className="text-2xl text-gray-400 hover:text-gray-800">×</button>
              </div>
            </div>
            <div className="flex gap-1 overflow-x-auto">
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => { setAdminTab(tab.id); setEditingPost(null); }}
                  className={`px-4 py-2 text-sm font-light whitespace-nowrap transition ${adminTab === tab.id ? 'text-gray-800 border-b-2 border-gray-800' : 'text-gray-400 hover:text-gray-600'}`}>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">

            {/* ---- ARTWORKS TAB ---- */}
            {adminTab === 'artworks' && (
              <div>
                {/* Collection category management */}
                <details className="mb-6 border border-gray-200 rounded-lg bg-gray-50">
                  <summary className="px-4 py-3 text-sm text-gray-600 cursor-pointer hover:text-gray-800">
                    Manage Collection Categories ({collectionCategories.length})
                  </summary>
                  <div className="px-4 pb-4 pt-2 space-y-2">
                    {collectionCategories.map(cat => (
                      <div key={cat.id} className="flex items-center gap-3 bg-white rounded px-3 py-2 border border-gray-100">
                        <span className="text-sm text-gray-700 flex-1">{cat.name}</span>
                        <span className="text-xs text-gray-400">{artworksByCategory(cat.id).length} works</span>
                        <button onClick={async () => {
                          if (artworksByCategory(cat.id).length > 0) {
                            alert('Remove all artworks from this collection first.');
                            return;
                          }
                          if (!window.confirm(`Remove "${cat.name}" collection?`)) return;
                          const updated = collectionCategories.filter(c => c.id !== cat.id);
                          setCollectionCategories(updated);
                          await saveSetting('collection_categories', JSON.stringify(updated));
                        }} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                      </div>
                    ))}
                    <div className="flex gap-2 mt-2">
                      <input id="new-collection-name" type="text" placeholder="New collection name" className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded" />
                      <button onClick={async () => {
                        const input = document.getElementById('new-collection-name');
                        const name = input.value.trim();
                        if (!name) return;
                        const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '');
                        if (ALL_CATEGORIES.some(c => c.id === id)) {
                          alert('A category with that name already exists.');
                          return;
                        }
                        const updated = [...collectionCategories, { id, name }];
                        setCollectionCategories(updated);
                        await saveSetting('collection_categories', JSON.stringify(updated));
                        input.value = '';
                      }} className="px-4 py-1.5 text-sm bg-gray-800 text-white rounded hover:bg-gray-900 transition">Add</button>
                    </div>
                  </div>
                </details>

                <div className="flex flex-wrap gap-2 mb-6">
                  {ALL_CATEGORIES.map(cat => (
                    <button key={cat.id} onClick={() => setAdminCategory(cat.id)}
                      className={`px-4 py-2 text-sm rounded-full transition ${adminCategory === cat.id ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      {cat.name} ({artworksByCategory(cat.id).length})
                    </button>
                  ))}
                </div>

                {catArtworks.map(artwork => (
                  <div key={artwork.id} className={`border rounded-lg p-5 mb-4 ${unsaved.has(artwork.id) ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex gap-4">
                      {/* Image upload */}
                      <div className="w-28 flex-shrink-0">
                        <ImageUploader
                          currentUrl={artwork.image_url}
                          folder={`artworks/${artwork.category}`}
                          label="Upload"
                          onUploaded={(url) => updateLocal(artwork.id, 'image_url', url)}
                        />
                      </div>
                      {/* Fields */}
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Title</label>
                          <input className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded" value={artwork.title || ''} onChange={e => updateLocal(artwork.id, 'title', e.target.value)} />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Medium</label>
                          <input className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded" value={artwork.medium || ''} onChange={e => updateLocal(artwork.id, 'medium', e.target.value)} placeholder="Oil on Canvas" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Size</label>
                          <input className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded" value={artwork.size || ''} onChange={e => updateLocal(artwork.id, 'size', e.target.value)} placeholder="60 × 80 cm" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Status</label>
                          <select className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded bg-white" value={artwork.status || 'portfolio'} onChange={e => updateLocal(artwork.id, 'status', e.target.value)}>
                            <option value="portfolio">Portfolio (Sold / Display Only)</option>
                            <option value="for_sale">Original For Sale</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Price (£)</label>
                          <input className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded" type="number" value={artwork.price || 0} onChange={e => updateLocal(artwork.id, 'price', parseFloat(e.target.value) || 0)} />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Sort Order</label>
                          <input className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded" type="number" value={artwork.sort_order || 0} onChange={e => updateLocal(artwork.id, 'sort_order', parseInt(e.target.value) || 0)} />
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end items-center mt-4 pt-3 border-t border-gray-200 gap-2">
                      {unsaved.has(artwork.id) && (
                        <button onClick={() => saveOne(artwork.id)} disabled={saving} className="px-4 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition disabled:opacity-50">Save Changes</button>
                      )}
                      <button onClick={() => deleteArtwork(artwork.id)} className="px-4 py-1.5 text-sm text-red-500 border border-red-200 rounded hover:bg-red-50 transition">Delete</button>
                    </div>

                    {/* Print Products for this artwork */}
                    {artwork.image_url && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-xs text-gray-400 mb-3 font-medium">PRINT PRODUCTS FOR SHOP</p>
                        {printProducts.filter(p => p.artwork_id === artwork.id).map(pp => (
                          <div key={pp.id} className="flex items-center gap-3 mb-2 bg-white rounded px-3 py-2 border border-gray-100">
                            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer flex-shrink-0">
                              <input type="checkbox" checked={pp.enabled} onChange={e => updatePrintProduct(pp.id, 'enabled', e.target.checked)} />
                            </label>
                            <span className="text-sm text-gray-700 flex-1">{pp.product_name}</span>
                            <span className="text-xs text-gray-400">{pp.sku}</span>
                            <span className="text-xs text-gray-400">£</span>
                            <input className="w-16 px-2 py-1 text-sm border border-gray-200 rounded text-right" type="number" value={pp.retail_price || 0}
                              onChange={e => updatePrintProduct(pp.id, 'retail_price', parseFloat(e.target.value) || 0)} />
                            <button onClick={() => deletePrintProduct(pp.id)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                          </div>
                        ))}
                        <details className="mt-2">
                          <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">+ Add print product</summary>
                          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1">
                            {PRODUCT_CATALOG.filter(cat => !printProducts.some(pp => pp.artwork_id === artwork.id && pp.sku === cat.sku)).map(cat => (
                              <button key={cat.sku} onClick={() => addPrintProduct(artwork.id, cat)} disabled={saving}
                                className="text-left px-3 py-2 text-xs text-gray-600 border border-gray-100 rounded hover:bg-gray-50 transition disabled:opacity-50">
                                {cat.name} — {cat.size} <span className="text-gray-400">(£{cat.defaultPrice})</span>
                              </button>
                            ))}
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                ))}

                {catArtworks.length === 0 && <p className="text-gray-400 text-center py-8 font-light">No artworks in {ALL_CATEGORIES.find(c => c.id === adminCategory)?.name} yet.</p>}

                <button onClick={() => addNewArtwork(adminCategory)} disabled={saving}
                  className="w-full py-3 mt-4 border-2 border-dashed border-gray-300 text-gray-500 rounded-lg hover:border-gray-400 hover:text-gray-700 transition font-light disabled:opacity-50">
                  + Add New Artwork
                </button>
              </div>
            )}

            {/* ---- JOURNAL TAB ---- */}
            {adminTab === 'journal' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-light text-gray-800">Journal Posts</h2>
                  <button onClick={addBlogPost} disabled={saving} className="px-4 py-2 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-900 transition disabled:opacity-50">+ New Post</button>
                </div>

                {Object.values(localPosts).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(post => (
                  <div key={post.id} className={`border rounded-lg mb-4 overflow-hidden ${editingPost === post.id ? 'border-gray-400' : unsavedPosts.has(post.id) ? 'border-amber-300' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer" onClick={() => setEditingPost(editingPost === post.id ? null : post.id)}>
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: post.published ? '#6B8F71' : '#d1d5db' }}></div>
                        <div>
                          <h3 className="text-sm font-light text-gray-800">{post.title || 'Untitled'}</h3>
                          <p className="text-xs text-gray-400">{new Date(post.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · {post.published ? 'Published' : 'Draft'}</p>
                        </div>
                      </div>
                      <span className="text-gray-400 text-sm">{editingPost === post.id ? '▲' : '▼'}</span>
                    </div>

                    {editingPost === post.id && (
                      <div className="p-5 border-t border-gray-200 space-y-4">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Title</label>
                          <input className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded" value={post.title} onChange={e => updateLocalPost(post.id, 'title', e.target.value)} />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Cover Image</label>
                          <ImageUploader currentUrl={post.cover_image} folder="blog" label="Upload Cover" onUploaded={(url) => updateLocalPost(post.id, 'cover_image', url)} />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Content</label>
                          <p className="text-xs text-gray-400 mb-2">Separate paragraphs with blank lines. Wrap poetry in *asterisks* for italic styling.</p>
                          <textarea className="w-full px-3 py-2 text-sm border border-gray-300 rounded" rows={10} value={post.content} onChange={e => updateLocalPost(post.id, 'content', e.target.value)} style={{ lineHeight: '1.6' }} />
                        </div>
                        <div className="flex items-center justify-between pt-2">
                          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                            <input type="checkbox" checked={post.published} onChange={e => updateLocalPost(post.id, 'published', e.target.checked)} /> Published
                          </label>
                          <div className="flex gap-2">
                            {unsavedPosts.has(post.id) && (
                              <button onClick={() => saveOnePost(post.id)} disabled={saving} className="px-4 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition disabled:opacity-50">Save Post</button>
                            )}
                            <button onClick={() => deleteBlogPost(post.id)} className="px-4 py-1.5 text-sm text-red-500 border border-red-200 rounded hover:bg-red-50 transition">Delete</button>
                          </div>
                        </div>
                        {post.content && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <p className="text-xs text-gray-400 mb-3">Preview:</p>
                            <div className="blog-content text-gray-600 font-light leading-relaxed text-sm bg-white p-4 rounded-lg border border-gray-100">
                              {renderBlogContent(post.content)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* ---- HERO TAB ---- */}
            {adminTab === 'hero' && (
              <div className="max-w-2xl">
                <h2 className="text-xl font-light text-gray-800 mb-6">Homepage Hero Image</h2>
                <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                  <ImageUploader currentUrl={localSettings.hero_image} folder="site" label="Upload Hero Image"
                    onUploaded={(url) => { setLocalSettings(prev => ({ ...prev, hero_image: url })); saveSetting('hero_image', url); }} />
                </div>
              </div>
            )}

            {/* ---- ABOUT TAB ---- */}
            {adminTab === 'about' && (
              <div className="max-w-2xl">
                <h2 className="text-xl font-light text-gray-800 mb-6">About Page</h2>
                <div className="border border-gray-200 rounded-lg p-6 bg-gray-50 space-y-5">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Background Image</label>
                    <ImageUploader currentUrl={localSettings.about_image} folder="site" label="Upload Background"
                      onUploaded={(url) => { setLocalSettings(prev => ({ ...prev, about_image: url })); saveSetting('about_image', url); }} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">About Text</label>
                    <textarea className="w-full px-3 py-2 text-sm border border-gray-300 rounded" rows={6} value={localSettings.about_text || ''}
                      onChange={e => setLocalSettings(prev => ({ ...prev, about_text: e.target.value }))} />
                  </div>
                  <button onClick={() => saveSetting('about_text', localSettings.about_text)} disabled={saving}
                    className="px-6 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-900 transition disabled:opacity-50">Save About Page</button>
                </div>
              </div>
            )}

            {/* ---- ENQUIRIES TAB ---- */}
            {adminTab === 'enquiries' && (
              <div>
                <h2 className="text-xl font-light text-gray-800 mb-6">Enquiries Received</h2>
                {commissions.length === 0 ? <p className="text-gray-400 text-center py-8 font-light">No enquiries yet.</p> : (
                  commissions.map(c => (
                    <div key={c.id} className="border border-gray-200 rounded-lg p-4 mb-3 bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{
                          backgroundColor: c.enquiry_type === 'purchase' ? '#fef3c7' : c.enquiry_type === 'print' ? '#dbeafe' : '#f3e8ff',
                          color: c.enquiry_type === 'purchase' ? '#92400e' : c.enquiry_type === 'print' ? '#1e40af' : '#6b21a8',
                        }}>
                          {c.enquiry_type === 'purchase' ? '🖼 Purchase' : c.enquiry_type === 'print' ? '🖨 Print' : '✉️ Commission'}
                        </span>
                        <span className="text-xs text-gray-400">{new Date(c.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <p className="text-sm text-gray-800"><strong>Email:</strong> {c.email}</p>
                      {c.artwork_title && <p className="text-sm text-gray-800"><strong>Artwork:</strong> {c.artwork_title}</p>}
                      {c.category && <p className="text-sm text-gray-800"><strong>Category:</strong> {c.category}</p>}
                      {c.message && <p className="text-sm text-gray-600 mt-1">{c.message}</p>}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* ---- ORDERS TAB ---- */}
            {adminTab === 'orders' && (
              <div>
                <h2 className="text-xl font-light text-gray-800 mb-6">Orders</h2>
                {orders.length === 0 ? <p className="text-gray-400 text-center py-8 font-light">No orders yet.</p> : (
                  orders.map(o => (
                    <div key={o.id} className="border border-gray-200 rounded-lg p-4 mb-3 bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{
                          backgroundColor: o.status === 'fulfilled' ? '#d1fae5' : o.status === 'submitted' ? '#dbeafe' : o.status === 'paid' ? '#fef3c7' : o.status === 'failed' ? '#fee2e2' : '#f3f4f6',
                          color: o.status === 'fulfilled' ? '#065f46' : o.status === 'submitted' ? '#1e40af' : o.status === 'paid' ? '#92400e' : o.status === 'failed' ? '#991b1b' : '#6b7280',
                        }}>
                          {o.status?.toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-400">{new Date(o.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <p className="text-sm text-gray-800"><strong>Customer:</strong> {o.customer_name || 'N/A'} ({o.customer_email})</p>
                      <p className="text-sm text-gray-800"><strong>Total:</strong> £{Number(o.total_amount).toFixed(2)}</p>
                      {o.prodigi_order_id && <p className="text-sm text-gray-600"><strong>Prodigi:</strong> {o.prodigi_order_id}</p>}
                      {o.items && (
                        <div className="mt-2">
                          {(Array.isArray(o.items) ? o.items : []).map((item, i) => (
                            <p key={i} className="text-xs text-gray-500">• {item.sku} (qty: {item.quantity})</p>
                          ))}
                        </div>
                      )}
                      {o.shipping_address && (
                        <p className="text-xs text-gray-400 mt-1">
                          Ship to: {o.shipping_address.line1}, {o.shipping_address.city}, {o.shipping_address.postal_code}, {o.shipping_address.country}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-pink-50 to-white text-gray-800 font-serif">

      {/* Lightbox */}
      {lightboxImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 cursor-pointer" onClick={() => setLightboxImage(null)}>
          <button className="absolute top-6 right-6 text-white text-3xl hover:text-gray-300 z-10" onClick={() => setLightboxImage(null)}>×</button>
          <img src={lightboxImage} alt="Artwork" className="max-w-full max-h-full object-contain" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* Admin button */}
      {!adminMode && (
        <button onClick={() => setAdminMode('login')} className="fixed bottom-4 right-4 z-40 p-3 bg-gray-800/60 text-white rounded-full hover:bg-gray-900 transition shadow-lg opacity-30 hover:opacity-100">⚙️</button>
      )}

      {/* Admin login */}
      {adminMode === 'login' && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-8 rounded-lg w-96 shadow-xl">
            <h2 className="text-2xl font-light mb-6">Admin Login</h2>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <input type="password" placeholder="Password" value={passwordInput} onChange={e => setPasswordInput(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm" autoFocus />
              <div className="flex gap-3">
                <button type="submit" className="flex-1 py-2 bg-gray-800 text-white rounded-lg text-sm">Login</button>
                <button type="button" onClick={() => setAdminMode(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin panel */}
      {adminMode === true && <AdminPanel />}

      {/* Commission Modal */}
      {showCommissionForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-8 rounded-lg w-full max-w-md shadow-xl">
            <h2 className="text-2xl font-light mb-6">Commission Enquiry</h2>
            <form onSubmit={handleCommissionSubmit} className="space-y-4">
              <input name="email" type="email" required placeholder="Your email" className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm" />
              <select name="category" required className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white" defaultValue="">
                <option value="" disabled>Select category</option>
                {ALL_CATEGORIES.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <textarea name="message" required placeholder="Tell us about your commission..." rows={4} className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm" />
              <div className="flex gap-3">
                <button type="submit" disabled={formSubmitting} className="flex-1 py-2 bg-gray-800 text-white rounded-lg text-sm disabled:opacity-50">{formSubmitting ? 'Sending...' : 'Send Enquiry'}</button>
                <button type="button" onClick={() => setShowCommissionForm(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Artwork Enquiry Modal */}
      {showEnquiryForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white p-8 rounded-lg w-full max-w-md shadow-xl">
            <h2 className="text-2xl font-light mb-2">{showEnquiryForm._printEnquiry ? 'Print Enquiry' : 'Purchase Enquiry'}</h2>
            <p className="text-gray-500 text-sm mb-4">"{showEnquiryForm.title}"</p>
            {showEnquiryForm.image_url && <img src={showEnquiryForm.image_url} alt="" className="w-full h-40 object-contain bg-gray-100 rounded-lg mb-4" />}
            <form onSubmit={handleArtworkEnquiry} className="space-y-4">
              <input name="email" type="email" required placeholder="Your email" className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm" />
              <textarea name="message" placeholder="Any questions or details? (optional)" rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm" />
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">{showEnquiryForm._printEnquiry ? `Print: £${Number(showEnquiryForm.print_price).toLocaleString()}` : `Original: £${Number(showEnquiryForm.price).toLocaleString()}`}</p>
                <p className="text-xs text-gray-400 mt-1">Rosanna will respond to your enquiry directly.</p>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={formSubmitting} className="flex-1 py-2 bg-gray-800 text-white rounded-lg text-sm disabled:opacity-50">{formSubmitting ? 'Sending...' : 'Send Enquiry'}</button>
                <button type="button" onClick={() => setShowEnquiryForm(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* NAVIGATION */}
      {/* ============================================================ */}
      <nav className="sticky top-0 bg-white/95 backdrop-blur border-b border-gray-100 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={() => navigateTo('home')} className="text-2xl font-light text-gray-800 tracking-wide">Rosanna Prescott</button>
            <div className="hidden md:flex gap-8 items-center">
              {[{ p: 'art', t: 'Fine Art', l: 'ART' }, { p: 'collections', t: 'Collections', l: 'COLLECTIONS' }, { p: 'about', t: 'About', l: 'ABOUT' }, { p: 'blog', t: 'Journal', l: 'JOURNAL' }, { p: 'shop', t: 'Shop', l: 'SHOP' }].map(i => (
                <button key={i.p} onClick={() => navigateTo(i.p, i.t)} className="text-xs tracking-widest text-gray-500 hover:text-gray-800 transition">{i.l}</button>
              ))}
              <button onClick={() => setShowCommissionForm(true)} className="text-xs tracking-widest text-gray-500 hover:text-gray-800 transition">COMMISSION</button>
            </div>
            <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-gray-600">{menuOpen ? <X size={24} /> : <Menu size={24} />}</button>
          </div>
          {sectionTitle && !selectedPost && (
            <div className="hidden md:flex items-center h-10 border-t border-gray-50">
              <button onClick={() => { setSelectedCategory(null); setSelectedPost(null); }} className="text-gray-400 hover:text-gray-600 mr-2"><ChevronLeft size={16} /></button>
              <h2 className="text-lg font-light text-gray-700">{sectionTitle}</h2>
            </div>
          )}
          {menuOpen && (
            <div className="md:hidden border-t border-gray-100 py-4 space-y-3">
              {[{ p: 'art', t: 'Fine Art', l: 'ART' }, { p: 'collections', t: 'Collections', l: 'COLLECTIONS' }, { p: 'about', t: 'About', l: 'ABOUT' }, { p: 'blog', t: 'Journal', l: 'JOURNAL' }, { p: 'shop', t: 'Shop', l: 'SHOP' }].map(i => (
                <button key={i.p} onClick={() => navigateTo(i.p, i.t)} className="block text-sm text-gray-600">{i.l}</button>
              ))}
              <button onClick={() => { setShowCommissionForm(true); setMenuOpen(false); }} className="block text-sm text-gray-600">COMMISSION</button>
            </div>
          )}
        </div>
      </nav>

      {/* ============================================================ */}
      {/* HOME */}
      {/* ============================================================ */}
      {currentPage === 'home' && (
        <div>
          <div className="relative overflow-hidden bg-gray-100" style={{ height: '80vh' }}>
            {settings.hero_image ? <img src={settings.hero_image} alt="Hero" className="w-full h-full object-cover" /> : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-50 to-pink-100"><p className="text-gray-400 font-light text-xl">Set hero image in admin panel</p></div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-white/40 to-transparent"></div>
          </div>

          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <h2 className="text-4xl font-light text-gray-800 mb-3 text-center">Fine Art</h2>
            <p className="text-gray-400 text-center mb-14 font-light">Original paintings & commissions</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {FINE_ART_CATEGORIES.map(cat => { const img = getCategoryImage(cat.id); return (
                <button key={cat.id} onClick={() => { setCurrentPage('art'); setSelectedCategory(cat.id); setSectionTitle(cat.name); }} className="group text-center">
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-4 shadow-md">
                    {img ? <img src={img} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" /> : <div className="w-full h-full flex items-center justify-center text-gray-300 text-4xl">🎨</div>}
                  </div>
                  <h3 className="text-xl font-light text-gray-800">{cat.name}</h3>
                </button>
              ); })}
            </div>
          </section>

          {collectionCategories.some(cat => artworksByCategory(cat.id).length > 0) && (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
              <h2 className="text-4xl font-light text-gray-800 mb-3 text-center">Collections</h2>
              <p className="text-gray-400 text-center mb-14 font-light">Curated bodies of work</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
                {collectionCategories.map(cat => { const img = getCategoryImage(cat.id); const cnt = artworksByCategory(cat.id).length; if (!cnt) return null; return (
                  <button key={cat.id} onClick={() => { setCurrentPage('collections'); setSelectedCategory(cat.id); setSectionTitle(cat.name); }} className="group text-center">
                    <div className="aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden mb-4 shadow-md">
                      {img ? <img src={img} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" /> : <div className="w-full h-full flex items-center justify-center text-gray-300 text-4xl">🎨</div>}
                    </div>
                    <h3 className="text-xl font-light text-gray-800">{cat.name}</h3>
                  </button>
                ); })}
              </div>
            </section>
          )}

          {publishedPosts.length > 0 && (
            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
              <h2 className="text-4xl font-light text-gray-800 mb-3 text-center">Journal</h2>
              <p className="text-gray-400 text-center mb-14 font-light">News, shows & reflections</p>
              <div className="space-y-8">
                {publishedPosts.slice(0, 2).map(post => (
                  <button key={post.id} onClick={() => { setCurrentPage('blog'); setSelectedPost(post); setSectionTitle('Journal'); }} className="w-full text-left group">
                    <div className="flex flex-col md:flex-row gap-6 items-start">
                      {post.cover_image && <div className="w-full md:w-48 h-32 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0"><img src={post.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-700" /></div>}
                      <div className="flex-1">
                        <p className="text-xs text-gray-400 mb-1">{new Date(post.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                        <h3 className="text-xl font-light text-gray-800 group-hover:text-gray-600 transition mb-2">{post.title}</h3>
                        <p className="text-sm text-gray-500 font-light" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.content?.split('\n\n')[0]}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="text-center mt-10">
                <button onClick={() => navigateTo('blog', 'Journal')} className="text-sm text-gray-500 hover:text-gray-800 transition tracking-widest">VIEW ALL POSTS →</button>
              </div>
            </section>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* ART — MASONRY */}
      {/* ============================================================ */}
      {currentPage === 'art' && !selectedCategory && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {FINE_ART_CATEGORIES.map(cat => { const img = getCategoryImage(cat.id); return (
              <button key={cat.id} onClick={() => { setSelectedCategory(cat.id); setSectionTitle(cat.name); }} className="group text-center">
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-4 shadow-md">
                  {img ? <img src={img} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" /> : <div className="w-full h-full flex items-center justify-center text-gray-300 text-4xl">🎨</div>}
                </div>
                <h3 className="text-xl font-light text-gray-800">{cat.name}</h3>
                <p className="text-sm text-gray-400">{artworksByCategory(cat.id).length} works</p>
              </button>
            ); })}
          </div>
        </div>
      )}
      {currentPage === 'art' && selectedCategory && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="masonry-grid">{artworksByCategory(selectedCategory).map(a => <ArtworkCard key={a.id} artwork={a} />)}</div>
          {artworksByCategory(selectedCategory).length === 0 && <p className="text-center text-gray-400 font-light py-16">No artworks in this category yet.</p>}
        </div>
      )}

      {/* COLLECTIONS — MASONRY */}
      {currentPage === 'collections' && !selectedCategory && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {collectionCategories.map(cat => { const img = getCategoryImage(cat.id); return (
              <button key={cat.id} onClick={() => { setSelectedCategory(cat.id); setSectionTitle(cat.name); }} className="group text-center">
                <div className="aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden mb-4 shadow-md">
                  {img ? <img src={img} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" /> : <div className="w-full h-full flex items-center justify-center text-gray-300 text-4xl">🎨</div>}
                </div>
                <h3 className="text-xl font-light text-gray-800">{cat.name}</h3>
                <p className="text-sm text-gray-400">{artworksByCategory(cat.id).length} works</p>
              </button>
            ); })}
          </div>
        </div>
      )}
      {currentPage === 'collections' && selectedCategory && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="masonry-grid">{artworksByCategory(selectedCategory).map(a => <ArtworkCard key={a.id} artwork={a} />)}</div>
        </div>
      )}

      {/* ABOUT */}
      {currentPage === 'about' && (
        <div className="relative min-h-[80vh] overflow-hidden flex items-center justify-center">
          {settings.about_image ? (<><img src={settings.about_image} alt="About" className="absolute inset-0 w-full h-full object-cover" /><div className="absolute inset-0 bg-black/40"></div></>) : (
            <div className="absolute inset-0 bg-gradient-to-br from-pink-50 to-pink-100"></div>
          )}
          <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
            <div className={`${settings.about_image ? 'bg-white/10 backdrop-blur-sm text-white' : 'text-gray-800'} p-8 md:p-12 rounded-lg`}>
              <h2 className="text-4xl md:text-5xl font-light mb-8">About</h2>
              <p className="text-lg md:text-xl font-light leading-relaxed whitespace-pre-line">{settings.about_text}</p>
            </div>
          </div>
        </div>
      )}

      {/* JOURNAL */}
      {currentPage === 'blog' && !selectedPost && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-4xl font-light text-gray-800 mb-12 text-center">Journal</h2>
          <div className="space-y-12">
            {publishedPosts.map(post => (
              <button key={post.id} onClick={() => { setSelectedPost(post); setSectionTitle(post.title); }} className="w-full text-left group">
                {post.cover_image && <div className="w-full h-56 md:h-72 bg-gray-100 rounded-lg overflow-hidden mb-5"><img src={post.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-700" /></div>}
                <p className="text-xs text-gray-400 mb-2 tracking-wider">{new Date(post.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                <h3 className="text-2xl font-light text-gray-800 group-hover:text-gray-600 transition mb-3">{post.title}</h3>
                <p className="text-gray-500 font-light leading-relaxed" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.content?.split('\n\n')[0]}</p>
                <span className="inline-block mt-3 text-sm text-gray-400 group-hover:text-gray-600 transition tracking-wider">Read more →</span>
              </button>
            ))}
            {publishedPosts.length === 0 && <p className="text-center text-gray-400 font-light py-16">No journal entries yet.</p>}
          </div>
        </div>
      )}
      {currentPage === 'blog' && selectedPost && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <button onClick={() => { setSelectedPost(null); setSectionTitle('Journal'); }} className="text-sm text-gray-400 hover:text-gray-600 transition mb-8 inline-flex items-center gap-1"><ChevronLeft size={14} /> Back to Journal</button>
          {selectedPost.cover_image && <div className="w-full rounded-lg overflow-hidden mb-8"><img src={selectedPost.cover_image} alt="" className="w-full h-auto" /></div>}
          <p className="text-xs text-gray-400 mb-3 tracking-wider">{new Date(selectedPost.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          <h1 className="text-3xl md:text-4xl font-light text-gray-800 mb-8">{selectedPost.title}</h1>
          <div className="blog-content text-gray-600 font-light leading-relaxed text-lg">{renderBlogContent(selectedPost.content)}</div>
        </div>
      )}

      {/* SHOP */}
      {currentPage === 'shop' && !shopArtwork && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-4xl font-light text-gray-800 mb-3 text-center">Shop</h2>
          <p className="text-gray-400 text-center mb-14 font-light">Fine art prints & framed prints, printed and shipped worldwide</p>

          {artworksWithPrints.length === 0 ? (
            <p className="text-center text-gray-400 font-light py-16">No prints available yet. Check back soon!</p>
          ) : (
            <div className="masonry-grid">
              {artworksWithPrints.map(artwork => (
                <div key={artwork.id} className="masonry-item group">
                  <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-4 shadow-md cursor-pointer"
                    onClick={() => setShopArtwork(artwork)}>
                    {artwork.image_url ? (
                      <img src={artwork.image_url} alt={artwork.title} className="w-full h-auto block group-hover:scale-105 transition duration-700" loading="lazy" />
                    ) : (
                      <div className="flex items-center justify-center py-24 text-gray-300"><span className="text-5xl">🎨</span></div>
                    )}
                  </div>
                  <h3 className="text-lg font-light text-gray-800 mb-1">{artwork.title}</h3>
                  <p className="text-sm text-gray-400 font-light mb-3">
                    From £{Math.min(...getArtworkProducts(artwork.id).map(p => Number(p.retail_price))).toLocaleString()}
                  </p>
                  <button onClick={() => setShopArtwork(artwork)}
                    className="w-full py-2 bg-gray-800 text-white text-sm font-light rounded-lg hover:bg-gray-900 transition">
                    View Prints
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SHOP — Product Detail */}
      {currentPage === 'shop' && shopArtwork && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <button onClick={() => setShopArtwork(null)} className="text-sm text-gray-400 hover:text-gray-600 transition mb-8 inline-flex items-center gap-1">
            <ChevronLeft size={14} /> Back to Shop
          </button>

          <div className="flex flex-col md:flex-row gap-10">
            {/* Image */}
            <div className="md:w-1/2">
              {shopArtwork.image_url ? (
                <img src={shopArtwork.image_url} alt={shopArtwork.title} className="w-full h-auto rounded-lg shadow-md cursor-pointer" onClick={() => setLightboxImage(shopArtwork.image_url)} />
              ) : (
                <div className="w-full h-80 bg-gray-100 rounded-lg flex items-center justify-center text-gray-300"><span className="text-5xl">🎨</span></div>
              )}
            </div>

            {/* Product options */}
            <div className="md:w-1/2">
              <h2 className="text-3xl font-light text-gray-800 mb-2">{shopArtwork.title}</h2>
              {shopArtwork.medium && <p className="text-sm text-gray-400 font-light mb-1">{shopArtwork.medium}</p>}
              <p className="text-gray-500 font-light mb-8">Select a size and format below to order a professionally printed reproduction.</p>

              {/* Fine Art Prints */}
              {getArtworkProducts(shopArtwork.id).filter(p => p.sku.includes('FAP')).length > 0 && (
                <div className="mb-8">
                  <h3 className="text-sm text-gray-500 tracking-widest mb-3">FINE ART PRINTS</h3>
                  <p className="text-xs text-gray-400 mb-3">Enhanced matte art paper, 200gsm. Museum quality.</p>
                  <div className="space-y-2">
                    {getArtworkProducts(shopArtwork.id).filter(p => p.sku.includes('FAP')).map(product => (
                      <div key={product.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-gray-400 transition">
                        <div>
                          <span className="text-sm text-gray-700">{product.size_label}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-light text-gray-800">£{Number(product.retail_price).toLocaleString()}</span>
                          <button onClick={() => handleCheckout(shopArtwork, product)} disabled={checkingOut}
                            className="px-4 py-1.5 bg-gray-800 text-white text-xs rounded hover:bg-gray-900 transition disabled:opacity-50">
                            {checkingOut ? 'Loading...' : 'Buy'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Framed Prints */}
              {getArtworkProducts(shopArtwork.id).filter(p => p.sku.includes('FRP')).length > 0 && (
                <div className="mb-8">
                  <h3 className="text-sm text-gray-500 tracking-widest mb-3">FRAMED PRINTS</h3>
                  <p className="text-xs text-gray-400 mb-3">Printed on fine art paper, professionally framed and ready to hang.</p>
                  <div className="space-y-2">
                    {getArtworkProducts(shopArtwork.id).filter(p => p.sku.includes('FRP')).map(product => (
                      <div key={product.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-gray-400 transition">
                        <div>
                          <span className="text-sm text-gray-700">{product.product_name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-light text-gray-800">£{Number(product.retail_price).toLocaleString()}</span>
                          <button onClick={() => handleCheckout(shopArtwork, product)} disabled={checkingOut}
                            className="px-4 py-1.5 bg-gray-800 text-white text-xs rounded hover:bg-gray-900 transition disabled:opacity-50">
                            {checkingOut ? 'Loading...' : 'Buy'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-400 mt-6">All prints are produced and shipped by Prodigi, a Fine Art Trade Guild approved printer. Worldwide shipping included. Typically delivered within 5–10 working days.</p>
            </div>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <footer className="bg-gray-800 text-white py-10 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400 font-light">&copy; {new Date().getFullYear()} Rosanna Prescott. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="mailto:rosannaprescott@gmail.com" className="text-sm text-gray-400 hover:text-white transition font-light">Contact</a>
              <button onClick={() => setShowCommissionForm(true)} className="text-sm text-gray-400 hover:text-white transition font-light">Commission</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RosannaPrescottArt;
